import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LIMITS } from '@kidzim/shared';
import { Badge, Button, Card, Input } from '../design-system/components';
import { api, type ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';

interface AccountData {
  id: string;
  email: string;
  displayName: string;
  role: string;
  createdAt: string;
  deletionScheduledAt: string | null;
  purgeAt: string | null;
  consent: { confirmed: boolean; awaitingConfirmation: boolean };
  emailVerified: boolean;
}
interface Session {
  id: string;
  userAgent: string | null;
  ip: string | null;
  createdAt: string;
  current: boolean;
}

function errText(err: unknown): string {
  const e = err as ApiError;
  if (e.error === 'invalid_password') return 'That password is incorrect.';
  if (e.error === 'email_unavailable') return 'That email is already in use.';
  if (e.error === 'invalid') return 'Please check the form.';
  return 'Something went wrong. Try again.';
}

export function Account() {
  const { reload, logout } = useAuth();
  const nav = useNavigate();
  const [account, setAccount] = useState<AccountData | null>(null);
  const [sessions, setSessions] = useState<Session[]>([]);

  async function load() {
    const a = await api<{ account: AccountData }>('/api/account');
    setAccount(a.account);
    setSessions((await api<{ sessions: Session[] }>('/api/account/sessions')).sessions);
  }
  useEffect(() => {
    load().catch(() => undefined);
  }, []);

  if (!account) return <p className="kz-muted">Loading…</p>;

  return (
    <div className="kz-stack" style={{ gap: 'var(--space-6)', maxWidth: 640 }}>
      <div>
        <span className="kz-eyebrow">Account</span>
        <h1 style={{ marginTop: 'var(--space-1)' }}>Your account</h1>
      </div>

      {account.deletionScheduledAt && (
        <Card style={{ background: 'var(--flag-100)', borderColor: 'var(--flag-500)' }}>
          <h3 style={{ color: 'var(--flag-500)' }}>Your account is scheduled for deletion</h3>
          <p className="kz-muted" style={{ font: 'var(--type-small)' }}>
            It will be permanently removed on {new Date(account.purgeAt!).toLocaleDateString()}. You can
            cancel any time before then.
          </p>
          <Button
            variant="secondary"
            size="sm"
            style={{ marginTop: 'var(--space-3)' }}
            onClick={async () => {
              await api('/api/account/delete/cancel', { method: 'POST' });
              await load();
            }}
          >
            Cancel deletion
          </Button>
        </Card>
      )}

      <EmailVerificationSection account={account} />
      <ConsentSection account={account} />
      <ProfileSection account={account} onSaved={async () => { await load(); await reload(); }} />
      <EmailSection account={account} onSaved={load} />
      <PasswordSection />
      <SessionsSection sessions={sessions} onChange={load} />

      <DangerZone scheduled={!!account.deletionScheduledAt} onDeleted={load} onSignOut={async () => { await logout(); nav('/'); }} />
    </div>
  );
}

function EmailVerificationSection({ account }: { account: AccountData }) {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  if (account.emailVerified) return null;

  return (
    <Card style={{ background: 'var(--sun-100)', borderColor: 'var(--sun-200)' }}>
      <div className="kz-row" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
        <h3>Verify your email</h3>
        <Badge tone="neutral">Unverified</Badge>
      </div>
      <p className="kz-muted" style={{ font: 'var(--type-small)', margin: 'var(--space-2) 0 var(--space-3)' }}>
        We emailed <strong>{account.email}</strong> a link to confirm you own this address. Didn’t get it? We can
        send it again.
      </p>
      <Button
        variant="secondary"
        size="sm"
        disabled={busy}
        onClick={async () => {
          setBusy(true);
          setMsg(null);
          try {
            await api('/api/account/verify-email/resend', { method: 'POST' });
            setMsg('Sent — check your email.');
          } catch {
            setMsg('Could not send right now. Try again shortly.');
          } finally {
            setBusy(false);
          }
        }}
      >
        {busy ? 'Sending…' : 'Resend verification email'}
      </Button>
      {msg && <p style={{ color: 'var(--safe-700)', font: 'var(--type-small)', marginTop: 'var(--space-2)' }}>{msg}</p>}
    </Card>
  );
}

function ConsentSection({ account }: { account: AccountData }) {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  if (account.consent.confirmed) {
    return (
      <Card>
        <div className="kz-row" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
          <h3>Parental consent</h3>
          <Badge tone="safe">Confirmed</Badge>
        </div>
        <p className="kz-muted" style={{ font: 'var(--type-small)', marginTop: 'var(--space-2)' }}>
          Thanks — your verifiable parental consent is on file. You can withdraw it any time by deleting a
          child or your account.
        </p>
      </Card>
    );
  }

  return (
    <Card style={{ background: 'var(--sun-100)', borderColor: 'var(--sun-200)' }}>
      <div className="kz-row" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
        <h3>Confirm your parental consent</h3>
        <Badge tone="neutral">Awaiting confirmation</Badge>
      </div>
      <p className="kz-muted" style={{ font: 'var(--type-small)', margin: 'var(--space-2) 0 var(--space-3)' }}>
        We emailed <strong>{account.email}</strong> a link to confirm you’re the grown-up and consent to KidzIM
        running your kids’ supervised chats. Didn’t get it? We can send it again.
      </p>
      <Button
        variant="secondary"
        size="sm"
        disabled={busy}
        onClick={async () => {
          setBusy(true);
          setMsg(null);
          try {
            await api('/api/account/consent/resend', { method: 'POST' });
            setMsg('Sent — check your email.');
          } catch {
            setMsg('Could not send right now. Try again shortly.');
          } finally {
            setBusy(false);
          }
        }}
      >
        {busy ? 'Sending…' : 'Resend confirmation email'}
      </Button>
      {msg && <p style={{ color: 'var(--safe-700)', font: 'var(--type-small)', marginTop: 'var(--space-2)' }}>{msg}</p>}
    </Card>
  );
}

function ProfileSection({ account, onSaved }: { account: AccountData; onSaved: () => Promise<void> }) {
  const [displayName, setDisplayName] = useState(account.displayName);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  return (
    <Card>
      <h3 style={{ marginBottom: 'var(--space-3)' }}>Profile</h3>
      <form
        className="kz-row"
        style={{ alignItems: 'flex-end', gap: 'var(--space-3)' }}
        onSubmit={async (e) => {
          e.preventDefault();
          setError(null);
          try {
            await api('/api/account/profile', { method: 'PATCH', body: JSON.stringify({ displayName }) });
            setSaved(true);
            setTimeout(() => setSaved(false), 1500);
            await onSaved();
          } catch (err) {
            setError(errText(err));
          }
        }}
      >
        <div className="kz-grow">
          <Input label="Your name" value={displayName} onChange={(e) => setDisplayName(e.target.value)} required />
        </div>
        <Button type="submit">Save</Button>
      </form>
      {saved && <p style={{ color: 'var(--safe-700)', font: 'var(--type-small)', marginTop: 'var(--space-2)' }}>Saved ✓</p>}
      {error && <p className="kz-error-text" style={{ marginTop: 'var(--space-2)' }}>{error}</p>}
    </Card>
  );
}

function EmailSection({ account, onSaved }: { account: AccountData; onSaved: () => Promise<void> }) {
  const [newEmail, setNewEmail] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [msg, setMsg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  return (
    <Card>
      <h3 style={{ marginBottom: 'var(--space-1)' }}>Email</h3>
      <p className="kz-muted" style={{ font: 'var(--type-small)', marginBottom: 'var(--space-3)' }}>
        Currently <strong>{account.email}</strong>. Changing it signs out your other devices.
      </p>
      <form
        className="kz-stack"
        onSubmit={async (e) => {
          e.preventDefault();
          setError(null);
          setMsg(null);
          try {
            await api('/api/account/email', { method: 'PATCH', body: JSON.stringify({ newEmail, currentPassword }) });
            setMsg('Email updated.');
            setNewEmail('');
            setCurrentPassword('');
            await onSaved();
          } catch (err) {
            setError(errText(err));
          }
        }}
      >
        <Input label="New email" type="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} required />
        <Input label="Current password" type="password" autoComplete="current-password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} required />
        <div>
          <Button type="submit" variant="secondary">Update email</Button>
        </div>
        {msg && <p style={{ color: 'var(--safe-700)', font: 'var(--type-small)' }}>{msg}</p>}
        {error && <p className="kz-error-text">{error}</p>}
      </form>
    </Card>
  );
}

function PasswordSection() {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [msg, setMsg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  return (
    <Card>
      <h3 style={{ marginBottom: 'var(--space-1)' }}>Password</h3>
      <p className="kz-muted" style={{ font: 'var(--type-small)', marginBottom: 'var(--space-3)' }}>
        Changing your password signs out your other devices.
      </p>
      <form
        className="kz-stack"
        onSubmit={async (e) => {
          e.preventDefault();
          setError(null);
          setMsg(null);
          try {
            await api('/api/account/password', { method: 'PATCH', body: JSON.stringify({ currentPassword, newPassword }) });
            setMsg('Password updated.');
            setCurrentPassword('');
            setNewPassword('');
          } catch (err) {
            setError(errText(err));
          }
        }}
      >
        <Input label="Current password" type="password" autoComplete="current-password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} required />
        <Input
          label="New password"
          type="password"
          autoComplete="new-password"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          minLength={LIMITS.MIN_PARENT_PASSWORD_LEN}
          required
        />
        <div>
          <Button type="submit" variant="secondary">Update password</Button>
        </div>
        {msg && <p style={{ color: 'var(--safe-700)', font: 'var(--type-small)' }}>{msg}</p>}
        {error && <p className="kz-error-text">{error}</p>}
      </form>
    </Card>
  );
}

function SessionsSection({ sessions, onChange }: { sessions: Session[]; onChange: () => Promise<void> }) {
  return (
    <Card>
      <div className="kz-row" style={{ justifyContent: 'space-between', marginBottom: 'var(--space-3)' }}>
        <h3>Active sessions</h3>
        {sessions.length > 1 && (
          <Button
            variant="secondary"
            size="sm"
            onClick={async () => {
              await api('/api/account/sessions/revoke-others', { method: 'POST' });
              await onChange();
            }}
          >
            Sign out other devices
          </Button>
        )}
      </div>
      <div className="kz-stack" style={{ gap: 'var(--space-2)' }}>
        {sessions.map((s) => (
          <div key={s.id} className="kz-row" style={{ justifyContent: 'space-between' }}>
            <span style={{ font: 'var(--type-small)' }} className="kz-grow">
              {shortAgent(s.userAgent)} {s.ip && <span className="kz-muted">· {s.ip}</span>}
            </span>
            {s.current ? <Badge tone="safe">This device</Badge> : <span className="kz-muted" style={{ font: 'var(--type-mono)', fontSize: 'var(--text-2xs)' }}>{new Date(s.createdAt).toLocaleDateString()}</span>}
          </div>
        ))}
      </div>
    </Card>
  );
}

function DangerZone({ scheduled, onDeleted, onSignOut }: { scheduled: boolean; onDeleted: () => Promise<void>; onSignOut: () => Promise<void> }) {
  const [confirming, setConfirming] = useState(false);
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);

  if (scheduled) return null;

  return (
    <Card style={{ borderColor: 'var(--flag-500)' }}>
      <h3 style={{ color: 'var(--flag-500)', marginBottom: 'var(--space-1)' }}>Delete account</h3>
      <p className="kz-muted" style={{ font: 'var(--type-small)', marginBottom: 'var(--space-3)' }}>
        This schedules your account — and your whole family, kids, rooms, and transcripts — for permanent
        deletion in 30 days. You can cancel by signing back in before then.
      </p>
      {!confirming ? (
        <Button variant="danger" onClick={() => setConfirming(true)}>Delete my account</Button>
      ) : (
        <form
          className="kz-stack"
          onSubmit={async (e) => {
            e.preventDefault();
            setError(null);
            try {
              await api('/api/account/delete', { method: 'POST', body: JSON.stringify({ currentPassword: password }) });
              await onDeleted();
            } catch (err) {
              setError(errText(err));
            }
          }}
        >
          <Input label="Confirm with your password" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required />
          {error && <p className="kz-error-text">{error}</p>}
          <div className="kz-row">
            <Button type="submit" variant="danger">Schedule deletion</Button>
            <Button type="button" variant="ghost" onClick={() => setConfirming(false)}>Keep my account</Button>
          </div>
        </form>
      )}
      <div style={{ marginTop: 'var(--space-4)', borderTop: 'var(--border-width) solid var(--border-subtle)', paddingTop: 'var(--space-3)' }}>
        <Button variant="ghost" size="sm" onClick={onSignOut}>Sign out</Button>
      </div>
    </Card>
  );
}

function shortAgent(ua: string | null): string {
  if (!ua) return 'Unknown device';
  if (/iPad/i.test(ua)) return 'iPad';
  if (/iPhone/i.test(ua)) return 'iPhone';
  if (/Android/i.test(ua)) return 'Android device';
  if (/Mac/i.test(ua)) return 'Mac';
  if (/Windows/i.test(ua)) return 'Windows PC';
  if (/Chrome/i.test(ua)) return 'Chrome browser';
  if (/Firefox/i.test(ua)) return 'Firefox browser';
  if (/Safari/i.test(ua)) return 'Safari browser';
  return 'Web browser';
}
